import { useEffect, useRef } from 'react'
import { Loader2, X, Trash2, Sparkles, StopCircle } from 'lucide-react'
import AgentMessage from './AgentMessage'
import { AGENT_DISPLAY_NAME } from '@/data/agentBranding'
import type { AgentMessage as AgentMessageType, AgentProgress, ConnectionStatus } from '@/hooks/useClaudeAgent'

const STATUS_LABELS: Record<string, string> = {
  thinking: 'Thinking...',
  responding: 'Responding...',
  read: 'Reading files...',
  write: 'Writing files...',
  edit: 'Editing files...',
  glob: 'Searching files...',
  grep: 'Searching content...',
  bash: 'Running command...',
  awaiting_permission: 'Awaiting permission...',
}

interface AgentStreamPanelProps {
  messages: AgentMessageType[]
  isStreaming: boolean
  wsStatus: ConnectionStatus
  error: string | null
  agentProgress: AgentProgress | null
  onCancel: () => void
  onClear: () => void
  onClose?: () => void
  onPermissionResponse?: (permissionId: string, decision: 'allow' | 'deny') => void
  title?: string
}

export default function AgentStreamPanel({
  messages,
  isStreaming,
  wsStatus,
  error,
  agentProgress,
  onCancel,
  onClear,
  onClose,
  onPermissionResponse,
  title = AGENT_DISPLAY_NAME,
}: AgentStreamPanelProps) {
  const chatEndRef = useRef<HTMLDivElement>(null)

  // Auto-scroll
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const formatElapsed = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m}m ${s}s`
  }

  const statusColor = wsStatus === 'connected' ? '#4caf50' :
    wsStatus === 'connecting' ? '#ff9800' : '#f44336'

  return (
    <div style={{
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: 'var(--color-bg-secondary)',
      borderRadius: 'var(--radius-lg)',
      overflow: 'hidden',
      border: '1px solid var(--color-border)',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.75rem 1rem',
        borderBottom: '1px solid var(--color-border)',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Sparkles size={16} style={{ color: 'var(--color-accent)' }} />
          <span style={{ fontWeight: 600, fontSize: '1rem' }}>{title}</span>
          <span style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: statusColor,
            display: 'inline-block',
          }} title={wsStatus} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          <button
            className="btn btn-ghost btn-sm"
            onClick={onClear}
            title="Clear history"
            disabled={isStreaming}
            style={{ padding: '4px 6px' }}
          >
            <Trash2 size={14} />
          </button>
          {onClose && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={onClose}
              style={{ padding: '4px 6px' }}
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      {isStreaming && agentProgress && (
        <div style={{
          padding: '0.5rem 1rem',
          borderBottom: '1px solid var(--color-border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: '0.8125rem',
          color: 'var(--color-text-secondary)',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
            <span>
              {STATUS_LABELS[agentProgress.status.toLowerCase()] || agentProgress.status}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span>{formatElapsed(agentProgress.elapsed)}</span>
            <button
              className="btn btn-ghost btn-sm"
              onClick={onCancel}
              style={{ padding: '2px 6px', color: 'var(--color-error, #cf222e)' }}
              title="Cancel"
            >
              <StopCircle size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{
          padding: '0.5rem 1rem',
          background: 'rgba(244, 67, 54, 0.08)',
          borderBottom: '1px solid rgba(244, 67, 54, 0.2)',
          fontSize: '0.875rem',
          color: 'var(--color-error, #cf222e)',
          flexShrink: 0,
        }}>
          {error}
        </div>
      )}

      {/* Messages */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        padding: '0.75rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        minHeight: 0,
      }}>
        {messages.length === 0 && !isStreaming && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            gap: '0.5rem',
            padding: '2rem',
          }}>
            <Sparkles size={28} style={{ color: 'var(--color-text-secondary)', opacity: 0.3 }} />
            <p style={{
              color: 'var(--color-text-secondary)',
              fontSize: '0.875rem',
              textAlign: 'center',
              margin: 0,
            }}>
              {AGENT_DISPLAY_NAME} will stream progress here as it works on your task.
            </p>
          </div>
        )}

        {messages.map((msg, idx) => (
          <AgentMessage
            key={idx}
            message={msg}
            onPermissionResponse={msg.role === 'permission' ? onPermissionResponse : undefined}
          />
        ))}

        <div ref={chatEndRef} />
      </div>
    </div>
  )
}
